"use client";

import React from "react";
import Image from "next/image";
import { useBreakpoint } from "@/shared/hooks/useBreakpoint";
import type { FeatureItem } from "@/shared/components/EndorsementBanner";

export type MarqueeBannerProps = {
    logoMobile?: string;
    logoDesktop?: string;
    bgColor?: string;
    accentBgColor?: string;
    features?: FeatureItem[];
    wedgeOffset?: number;
    wedgeOffsetSm?: number;
    wedgeOffsetMd?: number;
    wedgeOffsetLg?: number;
    wedgeOffsetXl?: number;
    speedSeconds?: number;
};

function FeatureIcon({ url }: { url: string }) {
    return <Image src={url} alt="" width={24} height={24} className="shrink-0" unoptimized />;
}

export default function MarqueeBanner(props: MarqueeBannerProps) {
    const {
        logoMobile,
        logoDesktop,
        bgColor,
        accentBgColor,
        features = [],
        wedgeOffset = 93,
        wedgeOffsetSm,
        wedgeOffsetMd,
        wedgeOffsetLg,
        wedgeOffsetXl,
        speedSeconds = 40
    } = props;

    const breakpoint = useBreakpoint();

    const getCurrentOffset = () => {
        switch (breakpoint) {
            case "sm":
                return wedgeOffsetSm ?? wedgeOffset;
            case "md":
                return wedgeOffsetMd ?? wedgeOffset;
            case "lg":
                return wedgeOffsetLg ?? wedgeOffset;
            case "xl":
            case "2xl":
                return wedgeOffsetXl ?? wedgeOffset;
            default:
                return wedgeOffset;
        }
    };

    if (!features.length) {
        return null;
    }

    return (
        <section>
            {/* Local marquee animation */}
            <style jsx>{`
                @keyframes marquee-scroll {
                    from {
                        transform: translateX(-50%);
                    }
                    to {
                        transform: translateX(0);
                    }
                }

                .marquee-track {
                    display: flex;
                    width: max-content;
                    animation: marquee-scroll linear infinite;
                    animation-duration: ${speedSeconds}s;
                }
            `}</style>

            <div className="relative mx-auto -mt-7 max-w-screen-xl md:-mt-10">
                {/* full-bleed bar */}
                <div className="relative left-1/2 ml-0 w-screen -translate-x-1/2" style={{ backgroundColor: bgColor }}>
                    {/* Logo positioned in max-width container */}
                    <div className="relative mx-auto max-w-screen-xl">
                        <div
                            className="pointer-events-none absolute inset-y-0 z-50 [clip-path:polygon(0_0,100%_0,85%_100%,0_100%)] [right:calc(100%-160px)] sm:[clip-path:polygon(0_0,95%_0,85%_100%,0_100%)] sm:[right:calc(100%-215px)] md:[clip-path:polygon(0_0,95%_0,90%_100%,0_100%)] md:[right:calc(100%-220px)] lg:[clip-path:polygon(0_0,100%_0,95%_100%,0_100%)] lg:[right:calc(100%-200px)] xl:[clip-path:polygon(0_0,100%_0,97%_100%,0_100%)] xl:[right:calc(100%-195px)]"
                            style={{
                                backgroundColor: accentBgColor,
                                width: "max(250px, 50vw)",
                                right: `${getCurrentOffset()}%`
                            }}
                        />
                        <div className="relative z-[99] mx-3 p-4">
                            <div className="flex items-center">
                                {logoMobile && <Image src={logoMobile} alt="logo" width={0} height={0} sizes="100vw" className="size-auto sm:hidden" unoptimized loading="lazy" />}
                                {logoDesktop && (
                                    <Image src={logoDesktop} alt="logo" width={0} height={0} sizes="100vw" className="hidden h-6 w-auto sm:block" unoptimized loading="lazy" />
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Marquee - full width */}
                    <div className="absolute inset-0 z-0 flex items-center">
                        <div className="w-full overflow-hidden">
                            <div className="marquee-track flex items-center gap-5 lg:gap-10">
                                {Array(4)
                                    .fill(features)
                                    .flat()
                                    .map((f, i) => (
                                        <div key={`${f.label}-${i}`} className="inline-flex shrink-0 items-center gap-2">
                                            <span className="mt-0.5">
                                                <FeatureIcon url={f.iconUrl} />
                                            </span>
                                            <span className="text-sm font-medium leading-5" style={f.textColor ? { color: f.textColor } : undefined}>
                                                {f.label}
                                            </span>
                                        </div>
                                    ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
